import React from 'react';
import { 
  Skeleton, 
  Card, 
  CardContent, 
  Box, 
  Stack 
} from '@mui/material';

interface LoadingSkeletonProps {
  variant?: 'list' | 'detail' | 'form' | 'grid' | 'dashboard';
  count?: number;
}

export const LoadingSkeleton: React.FC<LoadingSkeletonProps> = ({ 
  variant = 'list', 
  count = 3 
}) => {
  switch (variant) {
    case 'list':
      return (
        <Stack spacing={1}>
          {Array.from({ length: count }).map((_, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1 }}>
              <Skeleton variant="rounded" width={24} height={24} />
              <Skeleton variant="text" width={`${55 + (index % 3) * 15}%`} height={22} />
            </Box>
          ))}
        </Stack>
      );

    case 'detail':
      return (
        <Box sx={{ width: '100%' }}>
          {/* Tabs */}
          <Box sx={{ display: 'flex', gap: 3, borderBottom: 1, borderColor: 'divider', pb: 1, mb: 3 }}>
            <Skeleton variant="text" width={80} height={28} />
            <Skeleton variant="text" width={90} height={28} />
            <Skeleton variant="text" width={70} height={28} />
          </Box>

          {/* Content area */}
          <Card>
            <CardContent>
              <Stack spacing={3}>
                <Box>
                  <Skeleton variant="text" width="50%" height={28} />
                  <Skeleton variant="text" width="30%" height={18} sx={{ mt: 0.5 }} />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <Box key={index}>
                      <Skeleton variant="text" width={100} height={16} sx={{ mb: 0.5 }} />
                      <Skeleton variant="text" width="80%" height={20} />
                    </Box>
                  ))}
                </Box>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 1 }}>
                  <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: 1 }} />
                  <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: 1 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      );

    case 'form':
      return (
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Skeleton variant="text" width="40%" height={28} sx={{ mb: 1 }} />
              
              {Array.from({ length: count }).map((_, index) => (
                <Box key={index}>
                  <Skeleton variant="text" width={120} height={18} sx={{ mb: 0.5 }} />
                  <Skeleton variant="rectangular" width="100%" height={56} sx={{ borderRadius: 1 }} />
                </Box>
              ))}
              
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', pt: 1 }}>
                <Skeleton variant="rectangular" width={90} height={36} sx={{ borderRadius: 1 }} />
                <Skeleton variant="rectangular" width={90} height={36} sx={{ borderRadius: 1 }} />
              </Box>
            </Stack>
          </CardContent>
        </Card>
      );

    case 'grid':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 400 }}>
          {/* Toolbar: search field + action buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Skeleton variant="rectangular" width={240} height={36} sx={{ borderRadius: 1 }} />
            <Box sx={{ flex: 1 }} />
            <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
          </Box>
          {/* Table header */}
          <Skeleton variant="rectangular" width="100%" height={44} sx={{ mb: 0.5, borderRadius: 0.5 }} />
          {/* Table rows */}
          {Array.from({ length: count }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              width="100%"
              height={40}
              sx={{ mb: 0.5, borderRadius: 0.5, opacity: 1 - index * 0.06 }}
            />
          ))}
        </Box>
      );

    case 'dashboard':
      return (
        <Box>
          {/* Header bar */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Skeleton variant="text" width={220} height={36} />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Skeleton variant="rectangular" width={90} height={36} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" width={80} height={36} sx={{ borderRadius: 1 }} />
            </Box>
          </Box>
          {/* Quick actions bar */}
          <Skeleton variant="rectangular" width="100%" height={52} sx={{ borderRadius: 1, mb: 3 }} />
          {/* Metric cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} variant="rectangular" width="100%" height={110} sx={{ borderRadius: 1 }} />
            ))}
          </Box>
          {/* Charts row */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 1 }} />
          </Box>
        </Box>
      );

    default:
      return <Skeleton variant="rectangular" width="100%" height={200} />;
  }
};
